import { useContext, useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Form, Link, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { useDeepEffect } from '@domain-group/react-hooks'

import UserSearch from '@components/UserSearch'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ShareMediaSelectorButton from './components/ShareMediaSelectorButton'
import ShareQuoteButton from './components/ShareQuoteButton'

import useEmailRecipient from './hooks/useEmailRecipient'

const MODAL_KEY = 'SelectEmailRecipientModal'

export type ModalCallbacks = {
  closeModal: () => void,
  emailDashboardLink: (payload: HandleCallbackActionParams) => void,
  sendQuote: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const SelectEmailRecipientModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    campaign, campaignQuote, completeSelection, defaultSubject, endPoint, linkAnchor, preSelectedUsers,
  } = modalPayload

  const hookPayload = useEmailRecipient({ campaign })
  const {
    callbacks: {
      mapToRecipients,
      setModalState,
      toggleRecipient,
    },
    unselectedContacts,
    hasSelectedUsers,
    message,
    selectedRecipients,
    subject,
  } = hookPayload

  useDeepEffect(() => {
    if (preSelectedUsers){
      setModalState({ selectedRecipients: mapToRecipients(preSelectedUsers) })
    }
  }, [preSelectedUsers])

  useEffect(() => {
    if (defaultSubject){
      setModalState({ subject: defaultSubject })
    }
  }, [defaultSubject])

  const contacts = campaignQuote ? unselectedContacts : []

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Send" titleSecondLine="Email" />

      <SidebarModal.Content>
        <Form.Field label="Add Recipient">
          <UserSearch
            // @ts-expect-error TS2741
            callbacks={{
              selectItem: value => toggleRecipient(value),
            }}
            contacts={contacts}
            endpoint={endPoint}
            requestOptions={{
              exclude_brand_users: true,
            }}
            selectedIds={selectedRecipients.map(user => user.id)}
          />
        </Form.Field>

        {hasSelectedUsers && (
          <>
            <Form.Field
              direction="column"
              label="Selected Recipients"
              labelProps={{ marginBottom: 'none' }}
              marginTop="large"
            >
              {selectedRecipients.map((recipient) => {
                const {
                  email, full_name, id, image_url, type,
                } = recipient

                return (
                  <SidebarModal.User
                    disableHover
                    imageUrl={image_url}
                    key={`${type}-${id}`}
                    title={full_name}
                    subtitle={email || 'No Email'}
                  >
                    <Link
                      greyLink
                      // @ts-expect-error TS2345
                      onClick={() => toggleRecipient(recipient)}
                      textProps={{ fontSize: 'small' }}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Link>
                  </SidebarModal.User>
                )
              })}
            </Form.Field>

            {!campaignQuote && (
              <Form.Field direction="column" label="Message Subject">
                <input
                  onChange={e => setModalState({ subject: e.target.value })}
                  type="text"
                  value={subject}
                />
              </Form.Field>
            )}

            <Form.Field direction="column" label="Message" marginTop={campaignQuote ? 0 : 'large'}>
              <textarea
                onChange={e => setModalState({ message: e.target.value })}
                style={{ height: 80, resize: 'vertical' }}
                value={message}
              />
            </Form.Field>
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        {!!campaignQuote && (
          <ShareQuoteButton
            callbacks={callbacks}
            campaignQuote={campaignQuote}
            hookPayload={hookPayload}
          />
        )}

        {!campaignQuote && (
          <ShareMediaSelectorButton
            callbacks={callbacks}
            campaign={campaign}
            completeSelection={completeSelection}
            hookPayload={hookPayload}
            linkAnchor={linkAnchor}
          />
        )}
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <SelectEmailRecipientModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
